import Chest from "../entities/chest";
import Chicken from "../entities/chicken";
import GameItem from "../entities/gameItem";
import Entity from "../entities/entity";
import OtherPlayer from "../entities/otherPlayer";
import TestEntity from "../entities/testEntity";
import { ObjectToDraw, RegionData, WorldDescriptor } from "../types";
import { normalizeWorldDescriptor, saveJSON, generateEntityHash } from "../helpers";
import Chunk from "./chunk";
import ChunkFactory from "./chunkFactory";
import Spider from "../entities/spider";
import InteractionState from "./interactionState";

class GameMap{
    chunks: {[key: string]: Chunk} = {};

    //region cache sits in front of chunks as an I/O layer: each region bundles an
    //8x8 block of chunks and maps 1:1 to a region_<rx>_<ry>.json file.
    regions: {[key: string]: RegionData} = {};
    regionsQueue: {[key: string]: boolean} = {};

    worldName: string = '';
    posX: number = 0;
    posY: number = 0;
    renderDistance: number = 1;
    unloadChunkDistance: number = 2;

    worldDefaultData: WorldDescriptor | null = null;
    unpackChunks: boolean = true;

    constructor(){
        
    }

    loadWorld(worldName: string, targetX: number, targetY: number){
        this.worldName = worldName;
        this.posX = targetX;
        this.posY = targetY;
        this.chunks = {};
        this.regions = {};
        this.regionsQueue = {};
        entities = [];
        this.loadWorldDefaultData(worldName);
    }

    loadWorldDefaultData(world: string){
        p5js.loadJSON(`${staticServer}/assets/worlds/${world}/world.json`, (data) => {
            console.log(`Data for world.json loaded!`);
            console.log(data);
            //normalize so legacy string lists become {name, chance} entries
            this.worldDefaultData = normalizeWorldDescriptor(data);
        }, () => {
            console.error(`Failed to load JSON for world.json`);
            this.worldDefaultData = normalizeWorldDescriptor(null);
        });
    }

    loadVisibleChunks(){
        let currentChunk = {x: p5js.floor(this.posX / Chunk.CHUNK_SIZE), y: p5js.floor(this.posY / Chunk.CHUNK_SIZE)};
        
        for (let i = -this.renderDistance; i <= this.renderDistance; i++) {
            for (let j = -this.renderDistance; j <= this.renderDistance; j++) {
                let chunkX = currentChunk.x + i;
                let chunkY = currentChunk.y + j;
                this.ensureChunk(this.worldName, chunkX, chunkY);
            }
        }
    }

    unloadDistantChunks(){
        let currentChunk = {x: p5js.floor(this.posX / Chunk.CHUNK_SIZE), y: p5js.floor(this.posY / Chunk.CHUNK_SIZE)};
        let keysToRemove: {world: string, x: number, y: number}[] = [];
      
        for (let key in this.chunks) {
            let [worldName, chunkXs, chunkYs] = key.split('_');
            let chunkX = parseInt(chunkXs);
            let chunkY = parseInt(chunkYs);
            if (p5js.abs(chunkX - currentChunk.x) > this.unloadChunkDistance || p5js.abs(chunkY - currentChunk.y) > this.unloadChunkDistance) {
                keysToRemove.push({world: worldName, x: chunkX, y: chunkY});
            }
        }

        keysToRemove.forEach(key => {
            console.log("Unloading chunk", key);
            this.unloadChunk(key.x, key.y);
        });
    }

    unloadChunk(tileX: number, tileY: number){
        let chunkName = `${this.worldName}_${tileX}_${tileY}`;

        //remove entities in that area
        let posX = tileX * Chunk.CHUNK_SIZE;
        let posY = tileY * Chunk.CHUNK_SIZE;
        for(let i = 0; i < entities.length; i++){
            let entity = entities[i];
            if(entity.x >= posX && entity.x < posX + Chunk.CHUNK_SIZE && entity.y >= posY && entity.y < posY + Chunk.CHUNK_SIZE){
                entities.splice(i, 1);
                i--;
            }
        }

        if(this.chunks[chunkName]){
            delete this.chunks[chunkName];
        }
    }

    //region coordinate for a chunk (floor handles negative coordinates)
    regionCoords(tileX: number, tileY: number): {rx: number, ry: number}{
        return {
            rx: p5js.floor(tileX / Chunk.REGION_SIZE),
            ry: p5js.floor(tileY / Chunk.REGION_SIZE)
        };
    }

    regionKey(rx: number, ry: number): string{
        return `${this.worldName}_${rx}_${ry}`;
    }

    //chunk's key within its region, e.g. "0_3" (each component 0..REGION_SIZE-1)
    localKey(tileX: number, tileY: number): string{
        let {rx, ry} = this.regionCoords(tileX, tileY);
        return `${tileX - rx * Chunk.REGION_SIZE}_${tileY - ry * Chunk.REGION_SIZE}`;
    }

    //make sure the chunk exists in memory; loads its region first if needed.
    //chunks whose region is still in flight are built on a later frame.
    ensureChunk(world: string, tileX: number, tileY: number){
        let chunkName = `${world}_${tileX}_${tileY}`;
        if(this.chunks[chunkName]){
            return;
        }

        let {rx, ry} = this.regionCoords(tileX, tileY);
        if(this.regions[this.regionKey(rx, ry)]){
            this.buildChunk(world, tileX, tileY);
        }else{
            this.loadRegion(world, rx, ry);
        }
    }

    loadRegion(world: string, rx: number, ry: number){
        let regionName = this.regionKey(rx, ry);

        //check if region is already loaded or loading
        if(this.regions[regionName] || this.regionsQueue[regionName]){
            return;
        }

        this.regionsQueue[regionName] = true; // Mark as loading

        let path = `${staticServer}/assets/worlds/${world}/region_${rx}_${ry}.json`;

        p5js.loadJSON(path, (data) => {
            console.log(`Data for region ${path} loaded!`);
            this.regions[regionName] = this.normalizeRegion(data);
            delete this.regionsQueue[regionName]; // Remove loading mark
        }, () => {
            //missing region file -> empty region, its chunks generate procedurally
            console.error(`Failed to load JSON for region ${path}`);
            this.regions[regionName] = { chunks: {} };
            delete this.regionsQueue[regionName]; // Remove loading mark
        });
    }

    normalizeRegion(data: any): RegionData{
        if(data && data.chunks){
            return { chunks: data.chunks };
        }
        return { chunks: {} };
    }

    //build a single Chunk from its (already loaded) region, or procedurally
    //if that local key isn't present in the region.
    buildChunk(world: string, tileX: number, tileY: number){
        let chunkName = `${world}_${tileX}_${tileY}`;
        if(this.chunks[chunkName]){
            return;
        }

        let {rx, ry} = this.regionCoords(tileX, tileY);
        let region = this.regions[this.regionKey(rx, ry)];
        if(!region){
            return; //region not loaded yet
        }

        let chunkData = region.chunks[this.localKey(tileX, tileY)];
        if(chunkData){
            this.chunks[chunkName] = new Chunk(world, tileX, tileY, chunkData);
        }else{
            this.chunks[chunkName] = ChunkFactory.generateDefaultChunk(this.worldDefaultData, world, tileX, tileY);
        }
        this.unpackChunk(this.chunks[chunkName]);
    }

    //drop cached regions that no longer back any live chunk, so region-cache
    //lifetime follows chunk lifetime (no separate distance to tune).
    unloadOrphanRegions(){
        let liveRegions: {[key: string]: boolean} = {};
        for(let key in this.chunks){
            let chunk = this.chunks[key];
            let {rx, ry} = this.regionCoords(chunk.tileX, chunk.tileY);
            liveRegions[this.regionKey(rx, ry)] = true;
        }
        for(let regionName in this.regions){
            if(!liveRegions[regionName]){
                delete this.regions[regionName];
            }
        }
    }

    //persist a chunk by writing back the whole region it belongs to (one POST)
    saveChunk(chunk: Chunk){
        let {rx, ry} = this.regionCoords(chunk.tileX, chunk.tileY);
        let regionName = this.regionKey(rx, ry);

        let region = this.regions[regionName];
        if(!region){
            region = { chunks: {} };
            this.regions[regionName] = region;
        }

        region.chunks[this.localKey(chunk.tileX, chunk.tileY)] = chunk.data;
        chunk.isDefault = false;

        let path = `/assets/worlds/${this.worldName}/region_${rx}_${ry}.json`;
        console.log("Saving Region : ", path);
        saveJSON(region, path);
    }

    unpackChunk(chunk: Chunk){
        if(!this.unpackChunks){
            return;
        }
        //we are only interested in entities
        for(let entityData of chunk.data.entities){
            let entity: Entity | null = null;
            let posX = chunk.tileX * Chunk.CHUNK_SIZE + entityData.x;
            let posY = chunk.tileY * Chunk.CHUNK_SIZE + entityData.y;

            //entities placed with the editor may only carry "name",
            //generated/newer ones use "type" - accept both
            let kind = entityData.type || entityData.name;

            //stable, world-aware identity; skip entities the player already
            //killed/collected/opened so a reloaded chunk doesn't respawn them
            let hash = generateEntityHash(chunk.world, kind, posX, posY);
            if(InteractionState.isConsumed(hash)){
                continue;
            }

            if(kind == 'chicken'){
                entity = new Chicken(this, posX, posY);
            }
            else if(kind == 'spider'){
                entity = new Spider(this, posX, posY);
            }
            else if (kind == 'testEntity'){
                entity = new TestEntity(this, posX, posY);
            }
            else if (kind == 'otherPlayer'){
                entity = new OtherPlayer(posX, posY);
            }
            else if (kind == 'chest'){
                //a chest may define its contents in data.items, e.g.
                //{"items": ["coin", "coin", "heart"]} - GameItem types
                let items = (entityData.data && entityData.data.items) ? entityData.data.items : null;
                entity = new Chest(posX, posY, items);
            }
            else if (kind == 'item'){
                entity = new GameItem(entityData.variant, posX, posY, 0);
            }

            if(entity){
                //override the entity's own hash with the canonical world-aware
                //one and tag it persistent so kill/collect/open can record it
                entity.hash = hash;
                entity.persistent = true;
                if(!this.entityExists(hash)){
                    entities.push(entity);
                }
            }
        }
    }

    entityExists(hash: string){
        for(let entity of entities){
            if(entity.getHash() == hash){
                return true;
            }
        }
        return false;
    }

    getChunk(tileX: number, tileY: number): Chunk | null{
        let chunkName = `${this.worldName}_${tileX}_${tileY}`;
        if(!this.chunks[chunkName]){
            return null;
        }
        return this.chunks[chunkName];
    }

    getCurrentChunk(): Chunk | null{
        return this.getChunk(p5js.floor(this.posX / Chunk.CHUNK_SIZE), p5js.floor(this.posY / Chunk.CHUNK_SIZE));
    }

    update(){
        //console.log('Updating map');
        this.loadVisibleChunks();
        this.unloadDistantChunks();
        this.unloadOrphanRegions();
    }

    render(objectsToDraw: ObjectToDraw[]){
        for(let chunkName in this.chunks){
            this.chunks[chunkName].render(objectsToDraw, this.posX, this.posY);
        }
    }

    renderEditorMap(objectsToDraw: ObjectToDraw[], scale: number){
        for(let chunkName in this.chunks){
            this.chunks[chunkName].renderEditorMap(objectsToDraw, this.posX, this.posY, scale);
        }
    }

    getDebugText(){
        return `Chunks: ${Object.keys(this.chunks).length} Regions: ${Object.keys(this.regions).length}\n`;
    }
}

export default GameMap;